import { useMemo } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useAuthContext } from '@/lib/auth-context'
import ErrorBoundary from '@/components/ErrorBoundary'

const NAV_ITEMS = [
  { label: 'Home', to: '/portal/nominee', end: true },
  { label: 'Rally Board', to: '/portal/nominee/rally' },
  { label: 'Draw History', to: '/portal/nominee/history' },
  { label: 'My Link', to: '/portal/nominee/support' },
]

const ROLE_BADGE: Record<string, { label: string; color: string }> = {
  teacher: { label: 'Teacher', color: 'bg-emerald-100 text-emerald-800' },
  coach: { label: 'Coach', color: 'bg-indigo-100 text-indigo-800' },
  prospect: { label: 'Prospect', color: 'bg-sky-100 text-sky-800' },
  athlete: { label: 'Athlete', color: 'bg-sky-100 text-sky-800' },
  admin: { label: 'Admin', color: 'bg-stone-100 text-stone-800' },
  superadmin: { label: 'Admin', color: 'bg-stone-100 text-stone-800' },
}

export default function NomineePortalLayout() {
  const { profile } = useAuthContext() || {}
  const role = (profile?.role || '').toLowerCase()
  const badge = ROLE_BADGE[role] || { label: 'Member', color: 'bg-stone-100 text-stone-700' }

  const subtitle = useMemo(() => {
    if (role === 'teacher') return 'Your home while you wait for the draw. Rally support, check ceremony dates, and share your link.'
    if (role === 'coach') return 'Track your prospects in the pool, add new nominations, and rally community support.'
    if (role === 'prospect' || role === 'athlete') return 'Your coach is guiding you through the pool — support your journey with the rally board.'
    return 'Nominees waiting for the next draw ceremony.'
  }, [role])

  return (
    <div className="min-h-screen bg-gradient-to-br from-shell via-white to-shell/50">
      <div className="mx-auto max-w-5xl px-4 py-8">
        <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-cocoa/50">Nominee Portal</p>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-cocoa md:text-3xl">The Pool</h1>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge.color}`}>{badge.label}</span>
            </div>
            <p className="mt-1 text-sm text-cocoa/60">{subtitle}</p>
          </div>
        </header>

        <nav className="mb-6 flex flex-wrap gap-2 border-b border-sand/50 pb-4">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-cocoa text-white shadow'
                    : 'bg-white/70 text-cocoa/70 hover:bg-white hover:text-cocoa'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <ErrorBoundary fallbackPath="/portal/nominee" fallbackLabel="Back to The Pool">
          <Outlet />
        </ErrorBoundary>
      </div>
    </div>
  )
}
